import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Greek Call Center - English only
type Language = 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation files
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.first': 'First page',
    'common.last': 'Last page',
    'common.close': 'Close',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.confirm': 'Confirm',
    'common.clear': 'Clear',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.address': 'Address',
    'common.city': 'City',
    'common.country': 'Country',
    'common.notes': 'Notes',
    'common.total': 'Total',
    'common.quantity': 'Quantity',
    'common.price': 'Price',
    'common.product': 'Product',
    'common.products': 'Products',
    'common.order': 'Order',
    'common.orders': 'Orders',
    'common.lead': 'Lead',
    'common.leads': 'Leads',
    'common.user': 'User',
    'common.users': 'Users',
    'common.settings': 'Settings',
    'common.language': 'Language',
    'common.serbian': 'Serbian',
    'common.english': 'English',
    
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.orders': 'Orders',
    'nav.shipping': 'Shipping',
    'nav.naturatherapy-web': 'Naturatherapy Web',
    'nav.callCenter': 'Call Center',
    'nav.leads': 'Leads',
    'nav.products': 'Products',
    'nav.users': 'Users',
    'nav.analytics': 'Analytics',
    'nav.settings': 'Settings',
    'nav.logout': 'Logout',
    'nav.closeMenu': 'Close menu',
    'nav.openMenu': 'Open menu',

    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.overview': 'Overview',
    'dashboard.statistics': 'Statistics',
    'dashboard.description': 'Overview of your order monitoring system',
    'dashboard.timePeriod': 'Time Period',
    'dashboard.allTime': 'All Time',
    'dashboard.refreshCache': 'Refresh Cache',
    'dashboard.refresh': 'Refresh',
    'dashboard.loadingStatistics': 'Loading statistics...',
    'dashboard.cacheStatus': 'Cache Status:',
    'dashboard.refreshing': 'Refreshing...',
    'dashboard.active': 'Active',
    'dashboard.ordersCached': 'orders cached',
    'dashboard.lastUpdated': 'Last updated:',
    'dashboard.never': 'Never',
    'dashboard.autoRefresh': 'Auto-refresh:',
    'dashboard.showingDataFor': 'Showing data for:',

    // Analytics (call-center)
    'analytics.title': 'Analytics Dashboard',
    'analytics.description': 'Call center insights and performance metrics',
    'analytics.overview': 'Overview',
    'analytics.funnel': 'Funnel',
    'analytics.operators': 'Operators',
    'analytics.followups': 'Follow-ups',
    'analytics.tasks': 'Tasks',
    'analytics.quality': 'Quality',
    'analytics.dateRange': 'Date range',
    'analytics.today': 'Today',
    'analytics.yesterday': 'Yesterday',
    'analytics.last7': 'Last 7 days',
    'analytics.last30': 'Last 30 days',
    'analytics.last90': 'Last 90 days',
    'analytics.custom': 'Custom',
    'analytics.from': 'From',
    'analytics.to': 'To',
    'analytics.task': 'Task',
    'analytics.operator': 'Operator',
    'analytics.outcome': 'Outcome',
    'analytics.apply': 'Apply',
    'analytics.leadsCreated': 'Leads created',
    'analytics.leadsSentToCC': 'Sent to call center',
    'analytics.leadsCompleted': 'Leads completed',
    'analytics.callsEnded': 'Calls ended',
    'analytics.reachedCount': 'Reached',
    'analytics.reachRate': 'Reach rate',
    'analytics.ordersCreated': 'Orders created',
    'analytics.conversionRate': 'Conversion rate',
    'analytics.followUpsScheduled': 'Follow-ups scheduled',
    'analytics.followUpsOverdue': 'Overdue follow-ups',
    'analytics.funnelCreated': 'Created',
    'analytics.funnelSentToCC': 'Sent to CC',
    'analytics.funnelAttempted': 'Attempted',
    'analytics.funnelReached': 'Reached',
    'analytics.funnelOrderCreated': 'Order created',
    'analytics.funnelCompleted': 'Completed',
    'analytics.operatorsTable': 'Operators',
    'analytics.leadsTaken': 'Leads taken',
    'analytics.leadsCompletedCol': 'Completed',
    'analytics.attempts': 'Attempts',
    'analytics.reached': 'Reached',
    'analytics.callbacksScheduled': 'Callbacks scheduled',
    'analytics.overdueCallbacks': 'Overdue callbacks',
    'analytics.followupsToday': 'Today',
    'analytics.followupsTomorrow': 'Tomorrow',
    'analytics.followupsOverdue': 'Overdue',
    'analytics.tasksTable': 'Tasks',
    'analytics.leadsSent': 'Leads sent',
    'analytics.topFollowUpProducts': 'Top follow-up products',
    'analytics.topCities': 'Top cities',
    'analytics.qualityDuplicates': 'Duplicates',
    'analytics.qualityMissingAddress': 'Missing address',
    'analytics.qualityInvalidPhone': 'Invalid phone',
    'analytics.qualityBexLowConfidence': 'BEX low confidence',
    'analytics.qualityProblematic': 'Problematic',
    'analytics.qualityProblematicBreakdown': 'By reason',
    'analytics.loading': 'Loading...',
    'analytics.error': 'Failed to load data',
    'analytics.noData': 'No data for this period',
    'analytics.insights': 'Insights',
    'analytics.insights.revenue': 'Revenue',
    'analytics.insights.revenueByOperator': 'Revenue by operator',
    'analytics.insights.revenueByDay': 'Revenue by day',
    'analytics.insights.ordersAndProducts': 'Orders & products',
    'analytics.insights.ordersCreated': 'Orders created',
    'analytics.insights.totalQuantitySold': 'Total quantity sold',
    'analytics.insights.productBreakdown': 'Product breakdown',
    'analytics.insights.product': 'Product',
    'analytics.insights.quantity': 'Quantity',
    'analytics.insights.byDay': 'By day',
    'analytics.insights.orders': 'Orders',
    'analytics.insights.quantitySold': 'Quantity sold',
    'analytics.insights.performance': 'Operator performance',
    'analytics.insights.performanceNote': 'Calls count and average time between end of one call and end of next (no call start time stored).',
    'analytics.insights.calls': 'Calls',
    'analytics.insights.avgTimeBetweenCalls': 'Avg time between calls',
    'analytics.insights.callbackReasons': 'Callback reasons',
    'analytics.insights.totalCallbacksScheduled': 'Total callbacks scheduled',
    'analytics.insights.reason': 'Reason',
    'analytics.insights.count': 'Count',
    'analytics.insights.outcomes': 'Call outcomes',
    'analytics.insights.totalCalls': 'Total calls',
    'analytics.insights.byOutcome': 'By outcome',
    'analytics.insights.outcome': 'Outcome',
    'analytics.insights.notInterestedReasons': 'Not interested – reasons',
    'analytics.insights.shipping': 'Shipping & confirmation',
    'analytics.insights.ordersInPeriod': 'Orders in period',
    'analytics.insights.sentToShippingOrConfirmed': 'Sent to shipping / confirmed',
    'analytics.insights.waitingConfirmation': 'Waiting confirmation',
    
    // Orders
    'orders.title': 'Orders',
    'orders.monitorTitle': 'Natura Therapy Monitor',
    'orders.description': 'Real-time Order Tracking Dashboard',
    'orders.newOrder': 'New Order',
    'orders.addOrder': 'Add Order',
    'orders.bulkImport': 'Bulk Import',
    'orders.exporting': 'Exporting...',
    'orders.exportCSV': 'Export CSV',
    'orders.refreshingCache': 'Refreshing Cache...',
    'orders.customDateRange': 'Custom Date Range:',
    'orders.applyRange': 'Apply Range',
    'orders.showingResultsFor': 'Showing results for:',
    'orders.clearSearch': 'Clear search',
    'orders.searchPlaceholder': 'Search by name, ID, phone, or email...',
    'orders.switchToCardView': 'Switch to card view',
    'orders.switchToTableView': 'Switch to table view',
    'orders.sortDescending': 'Sort descending',
    'orders.sortAscending': 'Sort ascending',
    'orders.orderId': 'Order ID',
    'orders.customer': 'Customer',
    'orders.status': 'Status',
    'orders.date': 'Date',
    'orders.total': 'Total',
    'orders.createOrder': 'Create Order',
    'orders.shippingComment': 'Comment for BEX',
    'orders.shippingCommentPlaceholder': 'Optional note for shipping',
    'orders.requestedSendDate': 'When should the order be sent?',
    'orders.weekdaysOnly': 'Select a weekday (Mon–Fri), today or in the future.',
    'orders.potentialOrderHint': 'Potential order. Confirm on {{date}}. Lead stays in My Leads until then. Until that day you can get no-answer, callback, not interested, or "call me after X days".',
    'orders.needConfirmation': 'Need confirmation',
    'shipping.trackingCancelled': 'Cancelled',
    'orders.confirmOnDate': 'Confirm on date',
    'orders.confirmOnDateHint': 'Shipping will be the next working day after this date. Call Again is locked until this date.',
    'orders.editOrder': 'Edit Order',
    'orders.deleteOrder': 'Delete Order',
    'orders.statusNew': 'New Order',
    'orders.statusProcessing': 'Processing',
    'orders.statusCallback': 'Callback',
    'orders.statusApproved': 'Approved',
    'orders.statusCancelled': 'Cancelled',
    'orders.statusPackaging': 'Packaging',
    'orders.statusSending': 'Sending',
    'orders.statusTransfer': 'Transfer',
    'orders.statusDelivered': 'Delivered',
    'orders.statusCompleted': 'Completed',
    'orders.statusReturn': 'Return',
    
    // Leads
    'leads.title': 'Leads',
    'leads.newLead': 'New Lead',
    'leads.importLeads': 'Import Leads',
    'leads.sendToCallCenter': 'Send to Call Center',
    'leads.pageSize': 'Per page',
    'leads.availableLeadsPool': 'Available Leads Pool',
    'leads.takeThisLead': 'Take this Lead',
    'leads.leadProcessing': 'Lead Processing',
    'leads.identityContact': 'Identity & Contact',
    'leads.consentCompliance': 'Consent & Compliance',
    'leads.acquisitionAttribution': 'Acquisition & Attribution',
    'leads.relationshipBrand': 'Relationship to Brand',
    'leads.purchaseIntelligence': 'Purchase Intelligence',
    'leads.previousOrders': 'Previous Orders',
    'leads.latestOrderFirst': 'Latest order first',
    'leads.inTransit': 'In Transit',
    'leads.delivered': 'Delivered',
    'leads.canceled': 'Canceled',
    'leads.orderRejected': 'Rejected',
    'leads.ready': 'Ready',
    'leads.unconfirmed': 'Unconfirmed',
    'leads.createNewOrder': 'Create New Order',
    'leads.callNotes': 'Call Notes',
    'leads.callRating': 'Call Rating',
    'leads.callOutcome': 'Call Outcome',
    'leads.endCall': 'End Call',
    'leads.updateLeadContinue': 'Update Lead & Continue',
    'leads.redFlags': 'Red Flags',
    'leads.addFlag': 'Add Flag',
    'leads.bexAddress': 'BEX Address',
    'leads.validateBex': 'Validate with BEX',
    'leads.verified': 'Verified',
    'leads.municipality': 'Municipality',
    'leads.zipCode': 'ZIP Code',
    'leads.streetAddress': 'Street Address',
    'leads.fullName': 'Full Name',
    'leads.spokenName': 'Spoken Name',
    'leads.preferredLanguage': 'Preferred Language',
    'leads.contactChannel': 'Contact Channel',
    'leads.bestTimeToCall': 'Best Time to Call',
    'leads.timezone': 'Timezone',
    'leads.marketingConsent': 'Marketing Consent',
    'leads.doNotCall': 'Do Not Call',
    'leads.source': 'Source',
    'leads.campaign': 'Campaign',
    'leads.leadStatus': 'Lead Status',
    'leads.followUpDate': 'Follow-up Date',
    'leads.orderCount': 'Order Count',
    'leads.totalSpent': 'Total Spent',
    'leads.avgOrderValue': 'Average Order Value',
    'leads.lastOrder': 'Last Order',
    'leads.noPreviousOrders': 'No previous orders',
    'leads.addProduct': 'Add Product',
    'leads.orderItems': 'Order Items',
    'leads.discountNoteRequired': 'Discount Note Required',
    'leads.discountNotePlaceholder': 'e.g., Gift, Special offer...',
    'leads.successful': 'Successful',
    'leads.successfulWithConfirmation': 'Order needs Confirmation',
    'leads.busyLine': 'Busy line',
    'leads.wrongNumber': 'Wrong number',
    'leads.unreachable': 'The phone is unreachable',
    'leads.numberDoesntExist': "The number doesn't exist",
    'leads.profileInfo': 'Profile Info',
    'leads.generalNotes': 'General Notes',
    'leads.healthInfo': 'Health Info',
    'leads.comments': 'Comments',
    'leads.addHealthNote': 'Add health note...',
    'leads.addComment': 'Add comment...',
    'leads.enterGeneralNotes': 'Enter general notes about the lead...',
    'leads.settlement': 'Settlement',
    'leads.noAnswer': 'No Answer',
    'leads.callback': 'Callback Requested',
    'leads.notInterested': 'Not Interested',
    'leads.notInterestedReason': 'Reason',
    'leads.notInterestedReasonNoMoney': "Don't have money",
    'leads.notInterestedReasonStillHasProduct': 'Still has the product',
    'leads.notInterestedReasonProductDoesntHelp': "Product doesn't help him",
    'leads.notInterestedReasonDoNotCallAgain': 'Do not call again',
    'leads.notInterestedReasonBecauseOfDeath': 'Because of Death',
    'leads.notInterestedReasonOrdersForSomeoneElse': 'Orders for someone else',
    'leads.notInterestedReasonNotInterested': 'Not interested - not interested',
    'leads.notInterestedReasonOther': 'Other',
    'leads.other': 'Other',
    'leads.followUpNotes': 'Follow-up Notes',
    'leads.saveEndCall': 'Save & End Call',
    
    // Call Center
    'callCenter.title': 'Call Center',
    'callCenter.newLeads': 'New Leads',
    'callCenter.myLeads': 'My Leads',
    'callCenter.nextCall': 'Next Call',
    'callCenter.task': 'Task',
    'callCenter.typeOfLead': 'Type of Lead',
    'callCenter.lastCall': 'Last Call',
    'callCenter.successRate': 'Success Rate',
    'callCenter.maybeInterested': 'Maybe interested (what to try to sell on next call)',
    'callCenter.nextCallDate': 'Next call date',
    'callCenter.followUpMessage': 'Follow-up message',
    'callCenter.nextCallProduct': 'Next call product',
    'callCenter.followUp': 'Follow-up',
    'callCenter.callbackDetails': 'Callback details',
    'callCenter.callAgainAvailableFrom': 'Available from',
    'callCenter.needConfirmation': 'Need confirmation',
    'callCenter.lastEndCallStatus': 'Last End Call Status',
    'callCenter.needConfirmationUntil': 'Order must be confirmed on this date; Call Again available from this date.',
    'callCenter.orderNeedingConfirmation': 'Order needing confirmation',
    'callCenter.orderNeedingConfirmationHint': 'Order is pre-selected below. You can add, remove or edit items. Confirm (Successful) or mark Not interested with reason to clear.',
    'callCenter.removeItemConfirmTitle': 'Remove item from order?',
    'callCenter.removeItemConfirmMessage': 'Remove "{{name}}" from the order?',
    'callCenter.removeItemConfirmButton': 'Remove',
    'callCenter.bexAddressWrongCannotOrder': 'BEX address has missing fields (—). Fix the address to confirm or create an order.',
    'callCenter.fixAddressToCreateOrder': 'Fix the address so you can create an order.',
    'callCenter.interestedItems': 'Items of Interest',
    'callCenter.inCurrentMunicipality': 'In current municipality',
    'callCenter.sameCityInOtherMunicipality': 'Same city in other municipality',
    'callCenter.rejectOrder': 'Reject the order',
    'callCenter.rejectOrderConfirmTitle': 'Reject this order?',
    'callCenter.rejectOrderConfirmMessage': 'The order will be removed and product quantities will be restored to the warehouse. This cannot be undone.',
    'callCenter.rejectOrderConfirmButton': 'Reject order',
    'callCenter.confirmOrder': 'Confirm Order',
    'callCenter.confirmOrderConfirmTitle': 'Confirm this order?',
    'callCenter.confirmOrderConfirmMessage': 'The order will be marked as confirmed and will proceed to shipping. Call Again will be available.',
    'callCenter.confirmOrderConfirmButton': 'Confirm order',
    'common.removeItem': 'Remove item',
    
    // Settings
    'settings.title': 'Settings',
    'settings.configuration': 'Configuration',
    'settings.languageSettings': 'Language Settings',
    'settings.selectLanguage': 'Select Language',
    'settings.languageDescription': 'Choose your preferred language for the interface',
    'settings.available': 'available',
    'settings.here': 'here',

    // User Management (Users page + tabs)
    'users.pageTitle': 'User Management',
    'users.pageDescription': 'Manage users, teams, and organizational structure',
    'users.tabUsers': 'Users',
    'users.tabTeams': 'Teams',
    'users.tabHierarchy': 'Hierarchy',
    'users.tabStats': 'Statistics',
    'users.selectTab': 'Select a tab to view content',
    'users.refresh': 'Refresh',
    'users.management': 'Users Management',
    'users.managementDescription': 'Manage user accounts, roles, and permissions',
    'users.teamsManagement': 'Teams Management',
    'users.teamsManagementDescription': 'Manage teams, supervisors, and members',
    'users.hierarchyTitle': 'Organizational Hierarchy',
    'users.hierarchyDescription': 'View the organizational structure and team relationships',
    'users.statsTitle': 'User Statistics',
    'users.statsDescription': 'Overview of user activity and distribution',
    'users.addUser': 'Add User',
    'users.addTeam': 'Add Team',
    'users.loadingUsers': 'Loading users...',
    'users.loadingTeams': 'Loading teams...',
    'users.loadingHierarchy': 'Loading hierarchy...',
    'users.loadingStats': 'Loading statistics...',
    'users.search': 'Search',
    'users.searchUsers': 'Search users...',
    'users.searchTeams': 'Search teams...',
    'users.role': 'Role',
    'users.allRoles': 'All Roles',
    'users.country': 'Country',
    'users.allCountries': 'All Countries',
    'users.type': 'Type',
    'users.allTypes': 'All Types',
    'users.tableUser': 'User',
    'users.tableRole': 'Role',
    'users.tableCountries': 'Countries',
    'users.tablePermissions': 'Permissions',
    'users.tableStatus': 'Status',
    'users.tableLastLogin': 'Last Login',
    'users.tableActions': 'Actions',
    'users.tableTeam': 'Team',
    'users.tableSupervisor': 'Supervisor',
    'users.tableMembers': 'Members',
    'users.active': 'Active',
    'users.inactive': 'Inactive',
    'users.never': 'Never',
    'users.noPermissions': 'No permissions',
    'users.editUser': 'Edit user',
    'users.deactivate': 'Deactivate',
    'users.activate': 'Activate',
    'users.deleteUser': 'Delete user',
    'users.createNewUser': 'Create New User',
    'users.editUserTitle': 'Edit User',
    'users.accountInformation': 'Account Information',
    'users.username': 'Username',
    'users.email': 'Email',
    'users.password': 'Password',
    'users.profile': 'Profile',
    'users.firstName': 'First Name',
    'users.lastName': 'Last Name',
    'users.phone': 'Phone',
    'users.department': 'Department',
    'users.permissions': 'Permissions',
    'users.achievements': 'Achievements',
    'users.countries': 'Countries',
    'users.cancel': 'Cancel',
    'users.create': 'Create',
    'users.createTeam': 'Create Team',
    'users.creating': 'Creating...',
    'users.createNewTeam': 'Create New Team',
    'users.editTeam': 'Edit Team',
    'users.teamName': 'Team Name',
    'users.description': 'Description',
    'users.supervisor': 'Supervisor',
    'users.unknown': 'Unknown',
    'users.deleteTeamConfirm': 'Are you sure you want to delete this team?',
    'users.totalUsers': 'Total Users',
    'users.activeUsers': 'Active Users',
    'users.totalTeams': 'Total Teams',
    'users.recentLogins': 'Recent Logins',
    'users.recentLogins7Days': 'Recent Logins (7 days)',
    'users.usersByRole': 'Users by Role',
    'users.activityOverview': 'Activity Overview',
    'users.inactiveUsers': 'Inactive Users',
    'users.teamStatistics': 'Team Statistics',
    'users.teamTypes': 'Team Types',
    'users.averageTeamSize': 'Average Team Size',
    'users.usersPerTeam': 'users per team',
    'users.percentOfTotal': '% of total',
    'users.organizationalChart': 'Organizational Chart',
    'users.systemAdmin': 'System Admin',
    'users.generalManager': 'General Manager',
    'users.roleAdmins': 'Admins',
    'users.roleManagers': 'Managers',
    'users.roleCountryManagers': 'Country Managers',
    'users.rolePostManagers': 'Post Managers',
    'users.roleCallCenter': 'Call Center',
    'users.roleCallOperators': 'Call Operators',
    'users.rolePostOperators': 'Post Operators',
    'users.teamTypeCallCenter': 'Call Center',
    'users.teamTypePostOffice': 'Post Office',
    'users.teamTypeManagement': 'Management',
    'users.teamTypeSupport': 'Support',
    'users.formErrorRequired': 'Username, email, password, and role are required',
    'users.formErrorNameRequired': 'First name and last name are required',
    'users.formErrorEditRequired': 'Username, email, and role are required',
    'users.formErrorCountryRequired': 'Please select at least one country',
    'users.formErrorPermissionRequired': 'Please select at least one permission',
    'users.failedDeleteUser': 'Failed to delete user',
    'users.failedCreateUser': 'Failed to create user',
    'users.failedUpdateUser': 'Failed to update user',
    'users.failedToggleStatus': 'Failed to toggle user status',
    'users.confirmDeleteUser': 'Are you sure you want to delete this user?',
    'users.selectRole': 'Select Role',
    'users.updating': 'Updating...',
    'users.updateUser': 'Update User',
    'users.updateTeam': 'Update Team',
    'users.totalAccounts': 'Total Accounts',
    'users.profileInformation': 'Profile Information',

    // Common phrases
    'common.available': 'available',
    'common.here': 'here',
    'common.ifDifferent': 'If different from full name',
    'common.select': 'Select...',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.draft': 'Draft',
    'common.creating': 'Creating...',
    'common.saving': 'Saving...',
    'common.loadingProducts': 'Loading products...',
    'common.noPreviousOrders': 'No previous orders',
    'common.enterNotes': 'Enter notes about the call...',
    'common.selectOutcome': 'Select outcome',
    'common.followUpDate': 'Follow-up Date',
    'common.followUpHours': 'Hours (optional)',
    'common.followUpNotes': 'Follow-up Notes',
    'common.notesForFollowUp': 'Notes for the follow-up call...',
    'common.saveEndCall': 'Save & Next Call',
    'common.saveNextCall': 'Save & Next Call',
    'common.endCallSave': 'End Call & Save',
    'common.endCallExit': 'End Call & Exit',
    'common.endOnly': 'End only',
    'common.callNotes': 'Call Notes',
    'common.callRating': 'Call Rating',
    'common.callOutcome': 'Call Outcome',
    'common.flagType': "Flag type (e.g., 'Fraud', 'Duplicate', 'Abusive'):",
    'common.reason': 'Reason:',
    'common.validating': 'Validating...',
    'common.errorLoadingLead': 'Error Loading Lead',
    'common.loadingLeadDetails': 'Loading lead details...',
    'common.goBack': 'Go Back',
    'common.perUnit': 'per unit',
    'common.includes': 'Includes',
    'common.createOrder': 'Create Order',
    'common.addProduct': 'Add Product',
    'common.orderItems': 'Order Items',
    'common.discountNoteRequired': 'Discount Note Required',
    'common.discountNotePlaceholder': 'e.g., Gift, Special offer...',
    'common.addNew': 'Add New',
    'common.addComment': 'Add Comment',
    
    // Messages & Notifications
    'message.success': 'Success',
    'message.error': 'Error',
    'message.warning': 'Warning',
    'message.info': 'Information',
    'message.loading': 'Loading...',
    'message.saving': 'Saving...',
    'message.deleting': 'Deleting...',
    'message.processing': 'Processing...',
    'message.pleaseWait': 'Please wait...',
    'message.noData': 'No data available',
    'message.noResults': 'No results found',
    'message.searchPlaceholder': 'Search...',
    'message.selectAll': 'Select All',
    'message.deselectAll': 'Deselect All',
    'message.selected': 'selected',
    'message.items': 'items',
    'message.of': 'of',
    'message.page': 'Page',
    'message.perPage': 'per page',
    'message.showing': 'Showing',
    'message.to': 'to',
    'message.results': 'results',
    'message.available': 'available',
    'message.refresh': 'Refresh',
    'message.export': 'Export',
    'message.import': 'Import',
    'message.download': 'Download',
    'message.upload': 'Upload',
    'message.filter': 'Filter',
    'message.clear': 'Clear',
    'message.reset': 'Reset',
    'message.apply': 'Apply',
    'message.cancel': 'Cancel',
    'message.confirm': 'Confirm',
    'message.delete': 'Delete',
    'message.edit': 'Edit',
    'message.view': 'View',
    'message.details': 'Details',
    'message.close': 'Close',
    'message.save': 'Save',
    'message.update': 'Update',
    'message.create': 'Create',
    'message.add': 'Add',
    'message.remove': 'Remove',
    'message.continue': 'Continue',
    'message.finish': 'Finish',
    'message.next': 'Next',
    'message.previous': 'Previous',
    'message.back': 'Back',
    'message.submit': 'Submit',
    'message.send': 'Send',
    'message.yes': 'Yes',
    'message.no': 'No',
    'message.ok': 'OK',
    'message.retry': 'Retry',
    'message.tryAgain': 'Try Again',
    
    // Error Messages
    'error.generic': 'An error occurred',
    'error.network': 'Network error. Please check your connection.',
    'error.unauthorized': 'You are not authorized to perform this action.',
    'error.forbidden': 'Access forbidden.',
    'error.notFound': 'Resource not found.',
    'error.serverError': 'Server error. Please try again later.',
    'error.validation': 'Validation error',
    'error.required': 'This field is required',
    'error.invalid': 'Invalid value',
    'error.invalidEmail': 'Invalid email address',
    'error.invalidPhone': 'Invalid phone number',
    'error.invalidDate': 'Invalid date',
    'error.minLength': 'Minimum length is {{min}} characters',
    'error.maxLength': 'Maximum length is {{max}} characters',
    'error.loadFailed': 'Failed to load data',
    'error.saveFailed': 'Failed to save changes',
    'error.deleteFailed': 'Failed to delete',
    'error.createFailed': 'Failed to create',
    'error.updateFailed': 'Failed to update',
    'error.uploadFailed': 'Failed to upload file',
    'error.downloadFailed': 'Failed to download',
    'error.operationFailed': 'Operation failed',
    'error.pleaseTryAgain': 'Please try again',
    'error.fieldRequired': '{{field}} is required',
    'error.invalidFormat': 'Invalid format',
    
    // Success Messages
    'success.saved': 'Saved successfully',
    'success.created': 'Created successfully',
    'success.updated': 'Updated successfully',
    'success.deleted': 'Deleted successfully',
    'success.uploaded': 'Uploaded successfully',
    'success.downloaded': 'Downloaded successfully',
    'success.sent': 'Sent successfully',
    'success.imported': 'Imported successfully',
    'success.exported': 'Exported successfully',
    'success.operationComplete': 'Operation completed successfully',
    
    // Validation Messages
    'validation.required': 'This field is required',
    'validation.email': 'Please enter a valid email address',
    'validation.phone': 'Please enter a valid phone number',
    'validation.min': 'Minimum value is {{min}}',
    'validation.max': 'Maximum value is {{max}}',
    'validation.minLength': 'Minimum length is {{min}} characters',
    'validation.maxLength': 'Maximum length is {{max}} characters',
    'validation.pattern': 'Invalid format',
    'validation.match': 'Values do not match',
    
    // Leads specific
    'leads.noLeadsFound': 'No leads found',
    'leads.noLeadsMatching': 'No leads matching your criteria',
    'leads.loadingLeads': 'Loading leads...',
    'leads.failedToLoad': 'Failed to load leads',
    'leads.failedToCreate': 'Failed to create lead',
    'leads.failedToUpdate': 'Failed to update lead',
    'leads.failedToDelete': 'Failed to delete lead',
    'leads.failedToImport': 'Failed to import leads',
    'leads.importSuccess': '{{count}} lead(s) imported successfully',
    'leads.importFailed': 'Failed to import leads',
    'leads.selectLeads': 'Select leads to perform action',
    'leads.selectAtLeastOne': 'Please select at least one lead',
    'leads.sendingToCallCenter': 'Sending to call center...',
    'leads.sentToCallCenter': 'Lead(s) sent to call center successfully',
    'leads.failedToSend': 'Failed to send lead(s) to call center',
    'leads.confirmDelete': 'Are you sure you want to delete this lead?',
    'leads.confirmProcessLead': 'This action will take this lead as your lead and set you as the current operator. Continue?',
    'leads.confirmDeleteMultiple': 'Are you sure you want to delete {{count}} lead(s)?',
    'leads.deletedSuccessfully': 'Lead deleted successfully',
    'leads.deletedMultipleSuccessfully': '{{count}} lead(s) deleted successfully',
    'leads.stage': 'Stage',
    'leads.allStages': 'All Stages',
    'leads.problematic': 'Problematic',
    'leads.problematicBecause': 'Because of',
    'leads.abusiveCommunicationLabel': 'Abusive communication',
    'leads.notProblematic': 'Not Problematic',
    'leads.all': 'All',
    'leads.new': 'New',
    'leads.basic': 'Basic',
    'leads.good': 'Good',
    'leads.potential': 'Potential',
    'leads.customer': 'Customer',
    'leads.vip': 'VIP',
    'leads.address': 'Address',
    'leads.phone': 'Phone',
    'leads.email': 'Email',
    'leads.notes': 'Notes',
    'leads.createdAt': 'Created At',
    'leads.updatedAt': 'Updated At',
    'leads.actions': 'Actions',
    'leads.viewDetails': 'View Details',
    'leads.editLead': 'Edit Lead',
    'leads.deleteLead': 'Delete Lead',
    'leads.bulkSendToCallCenter': 'Send Selected to Call Center',
    'leads.bulkDelete': 'Delete Selected',
    'leads.moveToOperator': 'Move to other operator',
    'leads.moveToOperatorDescription': 'Assign the selected leads to another operator.',
    'leads.selectOperator': 'Select operator',
    'leads.moveToOperatorSuccess': 'Assigned {{count}} lead(s) to operator',
    'leads.selectTask': 'Select Task',
    'leads.taskDescription': 'What should operators do with these leads?',
    'leads.taskUpdateAddress': 'Update Address',
    'leads.taskSellNewProduct': 'Try to Sell New Product',
    'leads.taskCollectMoreData': 'Collect More Data',
    'leads.taskNewDose': 'Resell',
    'leads.taskCustom': 'Custom Task',
    'leads.taskUpdateaddress': 'Update Address',
    'leads.taskSellnewproduct': 'Try to Sell New Product',
    'leads.taskCollectmoredata': 'Collect More Data',
    'leads.taskNewdose': 'Resell',
    'leads.taskCustomPlaceholder': 'Enter custom task description...',
    'leads.send': 'Send',
    'leads.cancel': 'Cancel',
    'leads.importFromCSV': 'Import from CSV',
    'leads.export': 'Export',
    'leads.selectFile': 'Select File',
    'leads.fileSelected': 'File selected',
    'leads.noFileSelected': 'No file selected',
    'leads.uploading': 'Uploading...',
    'leads.importing': 'Importing...',
    'leads.importComplete': 'Import complete',
    'leads.importErrors': 'Import Errors',
    'leads.viewErrors': 'View Errors',
    'leads.totalImported': 'Total Imported',
    'leads.totalSkipped': 'Total Skipped',
    'leads.totalErrors': 'Total Errors',
    'leads.addNewLead': 'Add New Lead',
    'leads.name': 'Name',
    'leads.phoneNumber': 'Phone Number',
    'leads.emailAddress': 'Email Address',
    'leads.city': 'City',
    'leads.area': 'Area/Municipality',
    'leads.street': 'Street',
    'leads.addressNumber': 'Address Number',
    'leads.addressNumberFormat': 'Format: 5 or 5/10 for apartment',
    'leads.create': 'Create',
    'leads.update': 'Update',
    'leads.bexMatched': 'BEX Matched',
    'leads.bexNotMatched': 'BEX Not Matched',
    'leads.problematicReason': 'Problematic Reason',
    'leads.allReasons': 'All Reasons',
    'leads.reasonAddress': 'Address',
    'leads.operatorStatus': 'Operator Status',
    'leads.lastOperatorAction': 'Last Operator Action',
    'leads.lastOperator': 'Last Operator',
    'leads.currentOperator': 'Current Operator',
    'leads.getBackFromCallCenter': 'Get back from call center',
    'leads.getBackFromCallCenterSuccess': 'Lead returned from call center',
    'leads.moveBackFromCallCenter': 'Move back from call center',
    'leads.moveBackFromCallCenterSuccess': '{{count}} lead(s) moved back from call center',
    'leads.takeFromCallOperator': 'Take from call operator',
    'leads.heldByOperator': 'Held by operator',
    'leads.notHeldByOperator': 'Not held',
    'leads.inCallCenter': 'In call center',
    'leads.notInCallCenter': 'Not in call center',
    'leads.takeFromLeads': 'Take from leads',
    'leads.takeFromLeadsConfirm': 'Are you sure? This will assign this lead to you.',
    'leads.sortByLastOperator': 'Sort by last operator action time (click to cycle A-Z / Z-A / default)',
    'leads.sortAz': 'A-Z (oldest first)',
    'leads.sortZa': 'Z-A (newest first)',
    'leads.statusProcessing': 'Processing',
    'leads.statusEndWithSold': 'End with Sold',
    'leads.statusEndWithEdit': 'End with Edit',
    'leads.callOutcomeSuccessful': 'Successful',
    'leads.callOutcomeSuccessfulWithConfirmation': 'Order needs Confirmation',
    'leads.callOutcomeUnsuccessfulConfirmation': 'Unsuccessful confirmation',
    'leads.callOutcomeNoAnswer': 'No Answer',
    'leads.callOutcomeCallback': 'Callback Requested',
    'leads.callOutcomeNotInterested': 'Not Interested',
    'leads.callOutcomeOther': 'Other',
    'leads.createOrderAndEndCall': 'Create Order and End Call',
    
    // Call Center specific
    'callCenter.noNewLeads': 'No New Leads Available',
    'callCenter.allLeadsAssigned': 'All leads have been assigned. Check back later for new leads.',
    'callCenter.takeThisLead': 'Take this Lead',
    'callCenter.startNewCall': 'Start New Call',
    'callCenter.inCall': 'In Call',
    'callCenter.starting': 'Starting...',
    'callCenter.callInProgress': 'Call in progress - finish current call first',
    'callCenter.activeCallWarning': 'You have an active call. Finish it before taking another lead.',
    'callCenter.startCall': 'Start call with this lead',
    'callCenter.callAgain': 'Call Again',
    'callCenter.noPermission': 'No permission',
    'callCenter.filterByTask': 'Filter by Task',
    'callCenter.allTasks': 'All Tasks',
    'callCenter.allOperators': 'All Operators',
    'callCenter.updateAddress': 'Update Address',
    'callCenter.sellNewProduct': 'Sell New Product',
    'callCenter.collectMoreData': 'Collect More Data',
    'callCenter.newDose': 'Resell',
    'callCenter.customTask': 'Custom Task',
    'callCenter.searchLeads': 'Search leads...',
    'callCenter.yourAchievements': 'Your Achievements:',
    'callCenter.loadingLeads': 'Loading leads...',
    'callCenter.youCanTakeLeadsWithTask': 'You can take leads with "{{label}}" task',
    'callCenter.filterByCountry': 'Filter by Country',
    'callCenter.allCountries': 'All Countries',
    'callCenter.itemsPerPage': 'Items per page',
    'callCenter.refresh': 'Refresh',
    'callCenter.download': 'Download',
    'callCenter.orderId': 'Order ID',
    'callCenter.customerName': 'Customer Name',
    'callCenter.action': 'Action',
    'callCenter.noCallInfo': 'No call information available',
    'callCenter.lastCallDate': 'Last Call Date',
    'callCenter.lastCallOperator': 'Last Call Operator',
    'callCenter.lastCallDetails': 'Last Call Details',
    'callCenter.noPreviousCalls': 'No previous calls',
    'callCenter.operatorActions': 'Operator actions',
    'callCenter.previousStatusList': 'Previous status list',
    'callCenter.actionEndCall': 'End call',
    'callCenter.confirmationOutcome': 'Confirmation outcome',
    'callCenter.rejectedOrConfirmed': 'Rejected or Confirmed',
    'callCenter.confirmationRejected': 'Rejected',
    'callCenter.confirmationSuccess': 'Confirmed',
    'callCenter.confirmationPending': 'Pending',
    'callCenter.actionSkip': 'Skip (back)',
    'callCenter.sortNewestFirst': 'Newest first',
    'callCenter.sortOldestFirst': 'Oldest first',
    'callCenter.successfulCalls': 'Successful Calls',
    'callCenter.totalCalls': 'Total Calls',
    
    // Order specific
    'orders.noOrders': 'No orders found',
    'orders.loadingOrders': 'Loading orders...',
    'orders.failedToLoad': 'Failed to load orders',
    'orders.failedToCreate': 'Failed to create order',
    'orders.failedToUpdate': 'Failed to update order',
    'orders.failedToDelete': 'Failed to delete order',
    'orders.orderCreated': 'Order created successfully',
    'orders.orderUpdated': 'Order updated successfully',
    'orders.orderDeleted': 'Order deleted successfully',
    'orders.confirmDelete': 'Are you sure you want to delete this order?',
    'orders.customerName': 'Customer Name',
    'orders.product': 'Product',
    'orders.quantity': 'Quantity',
    'orders.price': 'Price',
    'orders.actions': 'Actions',
    'orders.viewOrder': 'View Order',
    'orders.createNewOrder': 'Create New Order',
    'orders.addProduct': 'Add Product',
    'orders.selectProduct': 'Select Product',
    'orders.productName': 'Product Name',
    'orders.unitPrice': 'Unit Price',
    'orders.totalPrice': 'Total Price',
    'orders.currency': 'Currency',
    'orders.country': 'Country',
    'orders.address': 'Address',
    'orders.city': 'City',
    'orders.area': 'Area',
    'orders.street': 'Street',
    'orders.zipCode': 'ZIP Code',
    'orders.phone': 'Phone',
    'orders.email': 'Email',
    'orders.gender': 'Gender',
    'orders.notSpecified': 'Not Specified',
    'orders.male': 'Male',
    'orders.female': 'Female',
    'orders.account': 'Account',
    'orders.source': 'Source',
    'orders.selectAccount': 'Select Account',
    'orders.selectSource': 'Select Source',
    'orders.orderSource': 'Order Source',
    'orders.manualEntry': 'Manual Entry (Outbound)',
    'orders.alterCPA': 'AlterCPA (Inbound)',
    'orders.required': 'Required',
    'orders.invalidPrice': 'Valid price is required',
    'orders.invalidQuantity': 'Valid quantity is required',
    'orders.productRequired': 'Product is required',
    'orders.productNameRequired': 'Product name is required',
    'orders.customerNameRequired': 'Customer name is required',
    'orders.phoneRequired': 'Phone is required',
    'orders.cityRequired': 'City is required',
    'orders.countryRequired': 'Country is required',
    'orders.accountRequired': 'Account is required',
    'orders.sourceRequired': 'Source is required',
    
    // Status messages
    'status.new': 'New',
    'status.pending': 'Pending',
    'status.processing': 'Processing',
    'status.completed': 'Completed',
    'status.cancelled': 'Cancelled',
    'status.failed': 'Failed',
    'status.active': 'Active',
    'status.inactive': 'Inactive',
    'status.packaging': 'Packaging',
    'status.sending': 'Sending',
    'status.transfer': 'Transfer',
    'status.delivered': 'Delivered',
    
    // Time & Date
    'time.today': 'Today',
    'time.yesterday': 'Yesterday',
    'time.thisWeek': 'This Week',
    'time.lastWeek': 'Last Week',
    'time.thisMonth': 'This Month',
    'time.lastMonth': 'Last Month',
    'time.thisYear': 'This Year',
    'time.custom': 'Custom Range',
    'time.from': 'From',
    'time.to': 'To',
    'time.selectDate': 'Select Date',
    'time.selectTime': 'Select Time',
    'time.date': 'Date',
    'time.time': 'Time',
    'time.datetime': 'Date & Time',
    
    // Common actions
    'action.view': 'View',
    'action.edit': 'Edit',
    'action.delete': 'Delete',
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.close': 'Close',
    'action.confirm': 'Confirm',
    'action.submit': 'Submit',
    'action.send': 'Send',
    'action.export': 'Export',
    'action.import': 'Import',
    'action.download': 'Download',
    'action.upload': 'Upload',
    'action.refresh': 'Refresh',
    'action.filter': 'Filter',
    'action.clear': 'Clear',
    'action.reset': 'Reset',
    'action.search': 'Search',
    'action.add': 'Add',
    'action.remove': 'Remove',
    'action.create': 'Create',
    'action.update': 'Update',
    'action.back': 'Back',
    'action.next': 'Next',
    'action.previous': 'Previous',
    'action.finish': 'Finish',
    'action.continue': 'Continue',
    
    // Page Not Found
    'error.pageNotFound': 'Page Not Found',
    'error.pageNotFoundDescription': "The page you're looking for doesn't exist.",
    
    // Dashboard
    'dashboard.accessDenied': 'Access Denied',
    'dashboard.noPermission': "You don't have permission to access the Dashboard.",
    'dashboard.failedToFetch': 'Failed to fetch statistics',
    'dashboard.errorConnecting': 'Error connecting to server',
    'dashboard.cacheServiceNotAvailable': 'Cache service not available (disabled)',
    'dashboard.errorRefreshingCache': 'Error refreshing cache',
    
    // Pickup Lists
    'pickupList.title': 'ACS Pickup Lists',
    'pickupList.description': 'Manage daily pickup lists and view history',
    'pickupList.totalVouchers': 'Total Vouchers (Selected Date)',
    'pickupList.printed': 'Printed',
    'pickupList.unprinted': 'Unprinted',
    'pickupList.today': 'Today',
    'pickupList.history': 'History',
    'pickupList.unprintedVouchers': 'Unprinted Vouchers:',
    'pickupList.cannotIssue': 'Cannot issue pickup list: Found {{count}} unprinted voucher(s). Please print or delete them first.',
    
    // Chat
    'chat.hello': "Hello! How can I help you today?",
    'chat.supportAgent': 'Support Agent',
  }
  // Serbian translations removed for Greek-CC (English only)
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    return 'en'; // Default to English for Greek-CC
  });

  useEffect(() => {
    localStorage.setItem('app_language', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation = translations[language][key] || key;
    
    if (params) {
      Object.keys(params).forEach((paramKey) => {
        translation = translation.replace(`{{${paramKey}}}`, String(params[paramKey]));
      });
    }
    
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
